import * as React from 'react';
import { NextPage } from 'next';
import { Dispatch } from 'redux';
import { useRouter } from 'next/router';
import Head from 'next/head';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { checkBookingSiteChatVisibility } from 'lib/util/chatChannelVisibility';
import * as url from 'url';

import config from 'config';
import { ReduxState } from 'ducks';
import { PmpProductDetails } from 'components/PrivateMarketplace/PmpProductDetails/PmpProductDetails';
import { PrivateMarketplaceLayout } from 'components/PrivateMarketplaceLayout/PrivateMarketplaceLayout';
import { wrapper } from 'ducks';
import {
  fetchSettingsWithChatLanguageAdjustmentServerSide,
  fetchProductServerSide,
  fetchReservationServerSide,
  fetchCustomPagesServerSide,
  fetchCustomTopPageServerSide,
} from 'ducks/server';
import { fetchReviews } from 'ducks/universal/reviews';
import { fetchConfigForHostname } from 'lib/util/fetchConfigForHostname';
import { getI18nServerSideProps } from 'i18n';
import { withSSR } from 'withSSR';
import { absoluteUrl } from 'lib/util/absoluetUrl';
import { CurrentUrlContext } from 'contexts/CurrentUrlContext';
import { languageOptionMap } from 'lib/util/i18nHelpers';
import { useCurrencyFormatter } from 'hooks/useCurrencyFormatter';
import { getWeekdayText } from 'lib/util/getWeekdayText';
import { selectBookingWidgetHeaderDescription } from 'lib/util/bookingWidgetDesign';
import { getProductPageOgpTags } from 'lib/util/getProductPageOgpTags';
import { Product } from 'models/product';
import { toProductShape } from 'lib/util/productShape';
import { Chat } from 'components/Chat/Chat';
import { ChatProvider } from 'components/Chat/ChatContext';
import { UrlHistoryProvider } from 'components/Map/useUrlQueryDisplayState';
import { TrackingWebSocketConnectionReceiver } from 'components/TrackingWebSocketConnectionReceiver/TrackingWebSocketConnectionReceiver';
import { ChatLanguageContextSSR } from 'lib/util/chatLanguageDetection';

interface Props {
  apiKey: string;
  host: string;
  ogpPromotionId: string;
  showOgpHighlight: boolean;
  product?: Product;
  noIndex?: boolean;
}

const ProductPage: NextPage<Props> = ({
  host,
  ogpPromotionId,
  showOgpHighlight,
  product,
  noIndex,
}) => {
  const router = useRouter();
  const url = `${host}${router.asPath}`;

  const { t, i18n } = useTranslation();
  const headerText = useSelector(selectBookingWidgetHeaderDescription);
  const settings = useSelector((state: ReduxState) => state.server.settings.all);
  const enableBookingWebsiteChat = useSelector(
    (state: ReduxState) => state.server.settings.all?.chat_settings?.enable_booking_website_chat
  );
  const chatSettings = useSelector((state: ReduxState) => state.server.settings.all?.chat_settings);
  const currentLanguage = i18n.language || 'en-US';
  const currencyFormatter = useCurrencyFormatter();
  const twitter = (settings?.sns_ids || []).find((snsId) => {
    return snsId?.key === 'twitter';
  });
  const description = product?.description || '';
  const dayOfWeek = product?.operating_days_of_week.length
    ? product.operating_days_of_week.map((weekday) => getWeekdayText(weekday, t)).join(', ')
    : '';
  let lowestPrice = product?.lowest_price_gross || '';
  if (lowestPrice) {
    lowestPrice = currencyFormatter.format(lowestPrice);
  }
  let ogpTags;
  if (product) {
    const productShape = toProductShape(product);
    ogpTags = getProductPageOgpTags(
      productShape,
      showOgpHighlight || false,
      ogpPromotionId || '',
      headerText,
      settings?.supplier_name || '',
      i18n.language || 'ja-JP',
      t,
      currencyFormatter
    );
  }

  return (
    <>
      <CurrentUrlContext.Provider
        value={{
          url,
        }}
      >
        <ChatProvider>
          <UrlHistoryProvider>
            <TrackingWebSocketConnectionReceiver />
            <Head>
              {config.enablePartnership && noIndex && <meta name="robots" content="noindex" />}
              <meta property="og:url" content={url} />
              <meta property="og:type" content={'article'} />
              <meta name="twitter:card" content={'summary_large_image'} />
              {ogpTags &&
                Object.entries(ogpTags).map(([key, value]) => (
                  <meta key={key} property={key} content={value} />
                ))}
              {dayOfWeek && (
                <meta
                  name="nutmeg:operating_days_of_week"
                  content={t('Days of Week') + ':' + dayOfWeek}
                />
              )}
              {lowestPrice && <meta name="nutmeg:lowest_price_gross" content={lowestPrice} />}
              {description && <meta name="description" content={description} />}
              {twitter && twitter?.id && <meta name="twitter:site" content={`@${twitter.id}`} />}
            </Head>
            <PrivateMarketplaceLayout>
              <PmpProductDetails host={host} />
            </PrivateMarketplaceLayout>
            <div
              style={
                {
                  '--header-height': '64px',
                } as React.CSSProperties
              }
            >
              {checkBookingSiteChatVisibility(
                chatSettings,
                currentLanguage,
                enableBookingWebsiteChat
              ) && <Chat />}
            </div>
          </UrlHistoryProvider>
        </ChatProvider>
      </CurrentUrlContext.Provider>
    </>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  async ({ params, query, req, store }) => {
    const hostname = req.headers?.host?.split(':')[0] || '';
    const { apiKey, lang } = await fetchConfigForHostname(hostname);

    const queries = url.parse(req.url || '', true).query;

    const productId = params?.id as string;
    const reservationId = query?.r as string;

    const acceptLanguageHeader = Array.isArray(req.headers['accept-language'])
      ? req.headers['accept-language'][0]
      : req.headers['accept-language'];

    const chatContext: ChatLanguageContextSSR = {
      pathname: `/top/products/${productId}`,
      pageLanguage: lang,
      acceptLanguageHeader,
    };

    const dispatch: Dispatch<any> = store.dispatch;
    await Promise.all([
      dispatch(
        fetchSettingsWithChatLanguageAdjustmentServerSide(
          apiKey,
          productId,
          lang,
          chatContext,
          `https://${hostname}`
        )
      ),
      dispatch(fetchProductServerSide(apiKey, productId, lang)),
      dispatch(
        fetchReviews(apiKey, lang, {
          productIds: [productId],
          pageSize: 5,
        })
      ),
      dispatch(fetchCustomPagesServerSide(apiKey, lang)),
      ...(reservationId ? [dispatch(fetchReservationServerSide(apiKey, reservationId, lang))] : []),
    ]);

    const isCustomTopPageEnabled = store.getState().server.settings.all?.is_custom_top_page_enabled;

    if (isCustomTopPageEnabled) {
      await dispatch(fetchCustomTopPageServerSide(apiKey, lang));
    }

    const i18nProps = await getI18nServerSideProps(lang);

    const product = store.getState().server.product.product;
    if (
      product?.booking_widget_settings?.blacklisted_languages?.some(
        (language) => languageOptionMap[language as keyof typeof languageOptionMap]?.iso === lang
      ) ||
      !product?.booking_widget_settings?.is_visible
    ) {
      return {
        redirect: {
          destination: '/top',
          permanent: false,
        },
      };
    }

    (req as any).locale = lang;

    const ogpPromotionId = queries?.promo && typeof queries.promo === 'string' ? queries.promo : '';
    const showOgpHighlight = typeof queries?.highlight === 'string';

    const noIndex = product?.supplier_id !== store.getState().server.settings.all.supplier_id;

    return {
      props: {
        apiKey,
        host: absoluteUrl(req),
        ogpPromotionId,
        showOgpHighlight,
        product,
        noIndex,
        ...i18nProps,
      },
    };
  }
);

export default withSSR()(ProductPage);
